// ─────────────────────────────────────────────────────────────
// Query Key Factory — hierarchical keys for React Query (TanStack).
//
// Based on TkDodo's query key factory pattern, adapted to match
// the SEGMENTS / PATHS convention from app/router/routes.ts.
//
// Usage:
//   queryKeys.professions.all            => ['professions']
//   queryKeys.professions.adminList(p)   => ['professions', 'admin-list', p]
//   queryKeys.professions.lookup()       => ['professions', 'lookup']
//   queryKeys.professions.detail(id)     => ['professions', 'detail', id]
//
// Invalidation:
//   queryClient.invalidateQueries({ queryKey: queryKeys.professions.all })
//   ^ Invalidates ALL profession queries (admin-list, lookup, detail)
//     because ['professions'] is a prefix of every key below it.
// ─────────────────────────────────────────────────────────────

export const queryKeys = {
  // ── Auth ──
  auth: {
    all: ['auth'] as const,
    session: () => ['auth', 'session'] as const,
    twoFactor: () => ['auth', '2fa'] as const
  },

  // ── Users & Roles ──
  users: {
    all: ['users'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['users', 'list', ...(filters ? [filters] : [])] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['users', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['users', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['users', 'summary-v2', ...(params ? [params] : [])] as const,
    lookup: () => ['users', 'lookup'] as const,
    lookupV2: () => ['users', 'lookup-v2'] as const,
    /** Asesores activos (`usr_`), para el selector de la ficha del cliente. */
    advisorsV2: () => ['users', 'advisors-v2'] as const,
    selectionV2: () => ['users', 'selection-v2'] as const,
    lookupAll: () => ['users', 'lookup-all'] as const,
    advisors: () => ['users', 'advisors'] as const,
    me: () => ['users', 'me'] as const,
    detail: (id: string | null) => ['users', 'detail', id] as const,
    username: () => ['users', 'username'] as const,
    email: () => ['users', 'email'] as const,
    searchUsersClients: (query?: string) =>
      ['users', 'search-clients', ...(query ? [query] : [])] as const
  },

  roles: {
    all: ['roles'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['roles', 'admin-list', ...(params ? [params] : [])] as const,
    lookup: () => ['roles', 'lookup'] as const,
    lookupV2: () => ['roles', 'lookup-v2'] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['roles', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['roles', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['roles', 'detail-v2', id] as const,
    detail: (id: string | null) => ['roles', 'detail', id] as const
  },

  // ── Companies & Agencies ──
  companies: {
    all: ['companies'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['companies', 'admin-list', ...(params ? [params] : [])] as const,
    lookup: () => ['companies', 'lookup'] as const,
    lookupV2: () => ['companies', 'lookup-v2'] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['companies', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['companies', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['companies', 'detail-v2', id] as const
  },

  agencies: {
    all: ['agencies'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['agencies', 'admin-list', ...(params ? [params] : [])] as const,
    lookup: () => ['agencies', 'lookup'] as const,
    lookupV2: () => ['agencies', 'lookup-v2'] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['agencies', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['agencies', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['agencies', 'detail-v2', id] as const
  },

  // ── Societies, Projects, Properties ──
  societies: {
    all: ['societies'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['societies', 'admin-list', ...(params ? [params] : [])] as const,
    lookup: () => ['societies', 'lookup'] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['societies', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['societies', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['societies', 'detail-v2', id] as const,
    lookupV2: () => ['societies', 'lookup-v2'] as const
  },

  /**
   * Cuentas de la app móvil (`cac_`).
   *
   * `all` existe para invalidar la familia entera desde una mutación: bloquear una cuenta
   * cambia su detalle, su fila en el listado Y los contadores de las tarjetas, y
   * enumerarlas una por una en cada mutación es la forma segura de olvidarse de alguna.
   */
  clientAccounts: {
    all: ['client-accounts'] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['client-accounts', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['client-accounts', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['client-accounts', 'detail-v2', id] as const,
    demoFirstRunV2: (id: string | null) => ['client-accounts', 'demo-first-run-v2', id] as const,
    byClientV2: (clientId: string | null) => ['client-accounts', 'by-client-v2', clientId] as const
  },

  projects: {
    all: ['projects'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['projects', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['projects', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['projects', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['projects', 'detail-v2', id] as const,
    lookupV2: () => ['projects', 'lookup-v2'] as const,
    active: () => ['projects', 'active'] as const,
    lookup: () => ['projects', 'lookup'] as const,
    detail: (id: string | null) => ['projects', 'detail', id] as const,
    template: (id: string) => ['projects', 'template', id] as const,
    withSaleClosureTemplate: () => ['projects', 'with-sale-closure-template'] as const
  },

  typeProperties: {
    all: ['type-properties'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['type-properties', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['type-properties', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['type-properties', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['type-properties', 'detail-v2', id] as const,
    lookupV2: () => ['type-properties', 'lookup-v2'] as const,
    lookup: () => ['type-properties', 'lookup'] as const,
    detail: (id: string | null) => ['type-properties', 'detail', id] as const,
    byProject: (projectId: string) => ['type-properties', 'by-project', projectId] as const,
    withTemplate: () => ['type-properties', 'with-template'] as const,
    withLineamientosTemplate: () => ['type-properties', 'with-lineamientos-template'] as const,
    withMaintenanceQuotaTemplate: () =>
      ['type-properties', 'with-maintenance-quota-template'] as const,
    withContractPromiseTemplate: () =>
      ['type-properties', 'with-contract-promise-template'] as const
  },

  properties: {
    all: ['properties'] as const,
    list: () => ['properties', 'list'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['properties', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['properties', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['properties', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['properties', 'detail-v2', id] as const,
    /**
     * El lookup v2. Los filtros van EN LA CLAVE porque cambian el resultado: pedir los
     * seleccionables para una negociación no devuelve lo mismo que pedir todos, y compartir
     * una clave entre las dos haría que el selector de una pantalla sirviera la lista de otra.
     */
    lookupV2: (params?: Record<string, unknown>) =>
      ['properties', 'lookup-v2', ...(params ? [params] : [])] as const,
    active: () => ['properties', 'active'] as const,
    available: () => ['properties', 'available'] as const,
    light: () => ['properties', 'light'] as const,
    detail: (id: string | null) => ['properties', 'detail', id] as const
  },

  // ── Representatives & Mandataries ──
  representatives: {
    all: ['representatives'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['representatives', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['representatives', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['representatives', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['representatives', 'detail-v2', id] as const,
    activeBySociety: (societyId: string) => ['representatives', 'active', societyId] as const
  },

  mandataries: {
    all: ['mandataries'] as const,
    list: () => ['mandataries', 'list'] as const,
    active: () => ['mandataries', 'active'] as const,
    detail: (id: string | null) => ['mandataries', 'detail', id] as const
  },

  // ── Reference data ──
  states: {
    all: ['states'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['states', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['states', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['states', 'summary-v2', ...(params ? [params] : [])] as const,
    lookup: () => ['states', 'lookup'] as const,
    lookupV2: () => ['states', 'lookup-v2'] as const,
    // `myTransitions` (v1) se fue el 2026-08-05 con su hook y su endpoint.
    /** `GET /api/v2/me/state-transitions`. Devuelve `sta_`; el de v1, el uuid legacy. */
    myTransitionsV2: () => ['states', 'my-transitions-v2'] as const,
    detail: (id: string | null) => ['states', 'detail', id] as const,
    detailV2: (id: string | null) => ['states', 'detail-v2', id] as const,
    /** Subrecurso: los requisitos documentales del portal de UN estado (`sta_`). */
    attachmentRequirementsV2: (id: string | null) =>
      ['states', 'attachment-requirements-v2', id] as const
  },

  workflows: {
    all: ['workflows'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['workflows', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['workflows', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['workflows', 'summary-v2', ...(params ? [params] : [])] as const,
    lookupV2: () => ['workflows', 'lookup-v2'] as const,
    lookup: () => ['workflows', 'lookup'] as const
  },

  creditTemplates: {
    all: ['credit-templates'] as const,
    current: (templateId: string | null = null) =>
      ['credit-templates', 'current', templateId] as const,
    details: (templateId: string) => ['credit-templates', 'details', templateId] as const,
    versions: (templateId: string) => ['credit-templates', 'versions', templateId] as const
  },

  professions: {
    all: ['professions'] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['professions', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['professions', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['professions', 'summary-v2', ...(params ? [params] : [])] as const,
    lookupV2: () => ['professions', 'lookup-v2'] as const,
    lookup: () => ['professions', 'lookup'] as const,
    detail: (id: string | null) => ['professions', 'detail', id] as const
  },

  countries: {
    all: ['countries'] as const,
    list: () => ['countries', 'list'] as const,
    detail: (id: string | null) => ['countries', 'detail', id] as const
  },

  departments: {
    all: ['departments'] as const,
    list: (countryId?: string) =>
      ['departments', 'list', ...(countryId ? [countryId] : [])] as const
  },

  municipalities: {
    all: ['municipalities'] as const,
    list: (departmentId?: string) =>
      ['municipalities', 'list', ...(departmentId ? [departmentId] : [])] as const
  },

  currencies: {
    all: ['currencies'] as const,
    list: () => ['currencies', 'list'] as const
  },

  products: {
    all: ['products'] as const,
    list: () => ['products', 'list'] as const,
    active: () => ['products', 'active'] as const,
    detail: (id: string | null) => ['products', 'detail', id] as const
  },

  subproducts: {
    all: ['subproducts'] as const,
    list: () => ['subproducts', 'list'] as const,
    active: () => ['subproducts', 'active'] as const,
    detail: (id: string | null) => ['subproducts', 'detail', id] as const
  },

  // `groups` se retiró el 2026-08-11 junto a su namespace de API (decisión Nº 31):
  // las cuatro claves quedaron sin consumidor al sacar el campo del alta de cliente.

  catalogs: {
    all: ['catalogs'] as const,
    list: () => ['catalogs', 'list'] as const
  },

  // ── Clients ──
  clients: {
    all: ['clients'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['clients', 'list', ...(filters ? [filters] : [])] as const,
    adminList: (params?: Record<string, unknown>) =>
      ['clients', 'admin-list', ...(params ? [params] : [])] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['clients', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['clients', 'summary-v2', ...(params ? [params] : [])] as const,
    selections: (filters?: Record<string, unknown>) =>
      ['clients', 'selections', ...(filters ? [filters] : [])] as const,
    lookupV2: (params?: Record<string, unknown>) =>
      ['clients', 'lookup-v2', ...(params ? [params] : [])] as const,
    detail: (id: string | null) => ['clients', 'detail', id] as const,
    detailV2: (id: string | null) => ['clients', 'detail-v2', id] as const,
    history: (id: string) => ['clients', 'history', id] as const
  },

  clientsJuridical: {
    all: ['clients-juridical'] as const,
    list: () => ['clients-juridical', 'list'] as const,
    detail: (id: string | null) => ['clients-juridical', 'detail', id] as const
  },

  // ── Negotiations ──
  negotiations: {
    all: ['negotiations'] as const,
    // `list` y `adminList` se retiraron el 2026-08-05 sin llamadores: el tablero usa
    // `adminListV2` desde la tajada 13c y el listado v1 detrás de `list` ya no existe.
    /**
     * El listado v2 del tablero. Árbol propio y no una variante de `adminList`:
     * aquél pagina por offset y filtra por el uuid legacy, éste por cursor y por
     * `sta_`. Compartir clave haría que una invalidación tocara la respuesta con
     * la identidad equivocada.
     */
    adminListV2: (params?: Record<string, unknown>) =>
      ['negotiations', 'admin-list-v2', ...(params ? [params] : [])] as const,
    /** Totales y facetas del tablero v2 (recurso separado del listado, ADR 0019). */
    summaryV2: (params?: Record<string, unknown>) =>
      ['negotiations', 'summary-v2', ...(params ? [params] : [])] as const,
    /** Las últimas N del dashboard: el listado v2 ordenado, no un recurso aparte. */
    recentV2: (limit: number) => ['negotiations', 'recent-v2', limit] as const,
    byClient: (clientId: string | null) => ['negotiations', 'by-client', clientId] as const,
    /** Las negociaciones de un cliente por v2: el listado filtrado por `ownerId`. */
    byClientV2: (clientId: string | null) => ['negotiations', 'by-client-v2', clientId] as const,
    approvedWithDelivery: () => ['negotiations', 'approved-with-delivery'] as const,
    detail: (id: string | null) => ['negotiations', 'detail', id] as const,
    // `historyStates` (v1, por `_id`) se fue con su último consumidor; queda `historyV2`.
    /** Historial v2, indexado por `neg_` — no comparte árbol con el de v1, que usa `_id`. */
    historyV2: (publicId: string | null) => ['negotiations', 'history-v2', publicId] as const,
    /** Los cinco bloques de formularios en UNA query, compartida por las pestañas de vista. */
    formsV2: (publicId: string | null) => ['negotiations', 'forms-v2', publicId] as const,
    /** El detalle v2, indexado por `neg_`. `detail` es el de v1 y se direcciona por `_id`. */
    detailV2: (publicId: string | null) => ['negotiations', 'detail-v2', publicId] as const,
    // Se indexa por `neg_` y no por el `_id` con el que se piden las otras: es la única
    // clave de esta familia que apunta a v2, y mezclar las dos identidades en un mismo
    // árbol de claves haría que una invalidación fallara en silencio.
    mobileAccess: (publicId: string | null) => ['negotiations', 'mobile-access', publicId] as const,
    /**
     * El PREFIJO de la familia, para invalidar el acceso móvil de todas las negociaciones
     * a la vez. Lo usan las mutaciones de la consola de cuentas: revocar un acceso desde
     * ahí cambia el estado de una negociación cuyo `neg_` no se conoce desde esa pantalla.
     *
     * Existe para que ese prefijo no se escriba a mano en el consumidor: un array literal
     * copiado deja de coincidir el día que esta clave cambie de forma, y la invalidación
     * no falla —simplemente no invalida nada, y la tarjeta de la negociación sigue
     * diciendo que el cliente tiene acceso después de que se lo quitaron.
     */
    mobileAccessAll: ['negotiations', 'mobile-access'] as const
  },

  // ── Credits ──
  credits: {
    all: ['credits'] as const,
    adminListV2: (params?: Record<string, unknown>) =>
      ['credits', 'admin-list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['credits', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['credits', 'detail-v2', id] as const
  },

  // ── Deliveries ──
  deliveries: {
    all: ['deliveries'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['deliveries', 'list', ...(filters ? [filters] : [])] as const,
    pending: () => ['deliveries', 'pending'] as const,
    completed: () => ['deliveries', 'completed'] as const,
    byNegotiation: (negotiationId: string) =>
      ['deliveries', 'by-negotiation', negotiationId] as const,
    detail: (id: string | null) => ['deliveries', 'detail', id] as const,
    // API v2: listado cursor + facetas. Van bajo la MISMA raíz `deliveries` a
    // propósito, para que invalidar la raíz alcance a los dos mundos mientras
    // convivan; separarlos haría que un cambio en v2 dejara la vista v1 vieja.
    listV2: (params?: Record<string, unknown>) =>
      ['deliveries', 'list-v2', ...(params ? [params] : [])] as const,
    summaryV2: (params?: Record<string, unknown>) =>
      ['deliveries', 'summary-v2', ...(params ? [params] : [])] as const,
    detailV2: (id: string | null) => ['deliveries', 'detail-v2', id] as const
  },

  // ── Availability & Reservations ──
  availability: {
    all: ['availability'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['availability', 'list', ...(filters ? [filters] : [])] as const,
    metrics: () => ['availability', 'metrics'] as const,
    monthlyActivity: () => ['availability', 'monthly-activity'] as const,
    detail: (id: string | null) => ['availability', 'detail', id] as const
  },

  inventoryComments: {
    all: ['inventory-comments'] as const,
    byProperty: (propertyId: string) => ['inventory-comments', 'by-property', propertyId] as const,
    // Clave aparte de la v1: el mismo inmueble tiene DOS direcciones (`_id` y `prp_`) y el árbol
    // que devuelve cada API no tiene la misma forma. Compartir caché mezclaría las dos.
    byPropertyV2: (propertyPublicId: string) =>
      ['inventory-comments', 'v2', 'by-property', propertyPublicId] as const
  },

  reservations: {
    all: ['reservations'] as const,
    byProperty: (propertyId: string) => ['reservations', 'by-property', propertyId] as const,
    history: (propertyId: string) => ['reservations', 'history', propertyId] as const,
    byToken: (token: string) => ['reservations', 'by-token', token] as const
  },

  // ── Files ──
  folders: {
    all: ['folders'] as const,
    list: (parentId?: string) => ['folders', 'list', ...(parentId ? [parentId] : [])] as const
  },

  attachments: {
    all: ['attachments'] as const,
    list: (folderId?: string) => ['attachments', 'list', ...(folderId ? [folderId] : [])] as const
  },

  // ── Reports ──
  reports: {
    all: ['reports'] as const
  },

  // ── Integrations ──
  sap: {
    all: ['sap'] as const
  },

  // ── Approval Flows ──
  approvalFlows: {
    all: ['approval-flows'] as const,
    /**
     * Los flujos de aprobación de UNA negociación.
     *
     * ☠️ **El identificador es parte de la clave, y hasta el 2026-08-05 no lo era.** `list()` no
     * recibía nada, así que las dos negociaciones compartían la entrada `['approval-flows',
     * 'list']`: abrir la impresión del expediente A y después la del B **pintaba los flujos de A**
     * mientras el refetch venía en camino. En una hoja que se imprime y se firma.
     *
     * Sin argumento sigue devolviendo el prefijo, y eso es a propósito: las invalidaciones
     * (`invalidateQueries({ queryKey: list() })`) alcanzan por prefijo a todas las negociaciones,
     * que es justo lo que quieren. Lo fija `query-keys.spec.ts`.
     */
    list: (negotiationId?: string | null) =>
      ['approval-flows', 'list', ...(negotiationId ? [negotiationId] : [])] as const,
    detail: (id: string | null, ...suffix: string[]) =>
      ['approval-flows', 'detail', id, ...suffix] as const
  },

  // ── Client Portal Forms ──
  portalForms: {
    all: ['portal-forms'] as const,
    detail: (token: string, ...suffix: string[]) =>
      ['portal-forms', 'detail', token, ...suffix] as const,
    documents: (token: string) => ['portal-forms', 'documents', token] as const,
    revalidationMetrics: () => ['portal-forms', 'revalidation-metrics'] as const
  },

  // ── Audit (bitácora central) ──
  audit: {
    all: ['audit'] as const,
    byEntity: (entityType: string, entityId: string) => ['audit', entityType, entityId] as const
  },

  // ── Flows (motor de flujos) ──
  flows: {
    all: ['flows'] as const,
    list: (scope?: string) => ['flows', 'list', ...(scope ? [scope] : [])] as const,
    detail: (id: string | null) => ['flows', 'detail', id] as const
  },

  // ── Documentos (cartapacio, ADR 0022) ──
  documents: {
    all: ['documents'] as const,
    /**
     * Lo que UN dueño posee. La clave lleva el par completo porque la misma pestaña se
     * monta cuatro veces con dueños distintos: sin el `ownerType` en la clave, un
     * `prj_` y un `prp_` homónimos compartirían caché.
     */
    byOwner: (ownerType: string, ownerId: string) =>
      ['documents', 'by-owner', ownerType, ownerId] as const,
    /**
     * La plantilla de categorías es GLOBAL: una sola para toda la plataforma, así que no lleva
     * dueño en la clave. Las cuatro pestañas comparten esta caché a propósito.
     */
    categories: () => ['documents', 'categories'] as const,
    /**
     * La URL firmada para VER una imagen. Por adjunto y no por dueño: la galería pide una por
     * foto y cada una vence sola, así que compartir clave las invalidaría todas juntas.
     */
    previewUrl: (attachmentId: string) => ['documents', 'preview-url', attachmentId] as const,
    /**
     * De dónde puede copiar su galería un dueño: la cadena por encima y los subtipos hermanos.
     *
     * Lleva el par completo por el mismo motivo que `byOwner`, y va SEPARADA de ella aunque las
     * dos hablen del mismo dueño: esta lista cambia cuando cambian OTROS dueños (alguien sube una
     * foto a un hermano), así que atarla a la clave del cartapacio haría que una subida propia
     * refrescara candidatos que no cambiaron, y al revés.
     */
    gallerySources: (ownerType: string, ownerId: string) =>
      ['documents', 'gallery-sources', ownerType, ownerId] as const,
    /**
     * Las hojas firmadas de la MINIATURA generada. Por adjunto, como la previsualización y por
     * el mismo motivo: cada firma vence sola, y compartir clave las invalidaría todas juntas.
     */
    thumbnailUrl: (attachmentId: string) => ['documents', 'thumbnail-url', attachmentId] as const,
    /**
     * Lo que un nivel HEREDA de sus ancestros. Lleva el par completo igual que `byOwner`: es otra
     * lectura del mismo dueño, y mezclarlas en una clave haría que publicar algo propio tirara
     * también la caché de lo heredado, que no cambió.
     */
    inherited: (ownerType: string, ownerId: string) =>
      ['documents', 'inherited', ownerType, ownerId] as const,
    /** La URL firmada para ABRIR el archivo en el visor. Audita, así que se pide al abrir. */
    downloadUrl: (attachmentId: string) => ['documents', 'download-url', attachmentId] as const
  },

  // ── Vista móvil (previsualización de la app del cliente) ──
  clientAppPreview: {
    all: ['client-app-preview'] as const,
    /** El contexto de una negociación: su `prp_` y el nombre del titular. Indexado por `neg_`. */
    negotiation: (negotiationPublicId: string | null) =>
      ['client-app-preview', 'negotiation', negotiationPublicId] as const,
    /** La portada del inmueble, con la galería FIRMADA. Indexada por `prp_`. */
    property: (propertyId: string | null) =>
      ['client-app-preview', 'property', propertyId] as const,
    /**
     * El cartapacio del inmueble, con las miniaturas firmadas. Árbol propio y NO una rama de
     * `documents`: aquél es la vista de curaduría del back-office (todo lo que existe, con su
     * visibilidad) y éste es lo que el CLIENTE ve. Compartir clave haría que publicar un documento
     * desde el cartapacio dejara la previsualización mostrando el árbol interno.
     */
    documents: (propertyId: string | null) =>
      ['client-app-preview', 'documents', propertyId] as const,
    /** La URL firmada de UN documento: por adjunto, porque cada firma vence sola. */
    download: (propertyId: string | null, documentId: string | null) =>
      ['client-app-preview', 'download', propertyId, documentId] as const,
    /**
     * El estado de cuenta del inmueble: el plan de pagos que SAP posee después del cierre.
     *
     * Indexado por `prp_` y no por `neg_` aunque el plan sea de una VENTA: el endpoint recibe el
     * inmueble y resuelve la venta adentro, igual que el carril del cliente. Cablear la clave a la
     * negociación acá haría que la consola y la app pudieran cachear cosas distintas para lo
     * mismo.
     */
    accountStatement: (propertyId: string | null) =>
      ['client-app-preview', 'account-statement', propertyId] as const
  },

  // ── Consola de experiencia de la app del cliente ──
  clientExperience: {
    all: ['client-experience'] as const,
    /**
     * La CURADURÍA de destacados de una negociación. Indexada por `neg_` y no por `prp_` porque la
     * configuración es de la venta cerrada: el mismo inmueble revendido tiene otra.
     *
     * Rama propia y NO una de `client-app-preview`: aquélla es lo que el CLIENTE ve, ésta es lo que
     * el back-office decide. Al guardar hay que invalidar las dos, y con una sola clave el
     * cartapacio del teléfono se quedaría mostrando el orden anterior.
     */
    featured: (negotiationPublicId: string | null) =>
      ['client-experience', 'featured', negotiationPublicId] as const,
    /**
     * La GALERÍA efectiva de la portada, con el origen de cada foto y el dueño al que se le puede
     * reescribir el orden. Indexada por `neg_` como su hermana, aunque la galería sea del inmueble:
     * el endpoint se direcciona así porque la guarda de venta cerrada es de la negociación.
     *
     * ☠️ Trae URLs FIRMADAS que viven quince minutos, así que no se puede tratar como dato frío: el
     * hook la marca obsoleta antes de ese vencimiento (ver `use-client-gallery.ts`).
     */
    gallery: (negotiationPublicId: string | null) =>
      ['client-experience', 'gallery', negotiationPublicId] as const,
    /**
     * QUÉ VE el cliente: los módulos y el cartapacio efectivo con su columna `visible`.
     *
     * Hermana de `featured` y no la misma clave aunque el servidor las guarde en la misma fila:
     * son dos endpoints, dos paneles y dos guardados. Compartir clave haría que guardar
     * destacados invalidara «Qué ve» y al revés, y cada guardado costaría dos lecturas.
     */
    visibility: (negotiationPublicId: string | null) =>
      ['client-experience', 'visibility', negotiationPublicId] as const
  },

  // ── Dashboard ──
  dashboard: {
    all: ['dashboard'] as const,
    recentClients: () => ['dashboard', 'recent-clients'] as const
  }
} as const
